import { useEffect, useRef, type ReactNode } from 'react';
import { Portal } from './portal';

/**
 * **The office dashboard's modal, on the operator surface** —
 * `dashboard/src/components/ui/modal.tsx`, copied rather than reinvented, along
 * with `.modal` / `.modal-panel` / `.modal-head` / `.modal-actions` in app.css.
 * This app has one caller (the New organization form), and a second grammar for
 * "a box over the page" would be a second set of keyboard bugs to find.
 *
 * **Keyboard behavior lives here rather than in the caller.** A dialog that
 * says `aria-modal="true"` is promising that nothing behind it is reachable, and
 * three things are what keep that promise:
 *
 *  1. **Initial focus.** The first focusable thing inside the panel, or the
 *     panel itself when there is none. It carries `tabIndex={-1}` for exactly
 *     that case.
 *  2. **The trap.** Tab and Shift+Tab wrap within the panel. The stops are
 *     computed on each press rather than once, because a modal's controls come
 *     and go — the Create button swaps for "Creating…", an error line appears.
 *  3. **Return.** Whatever had focus when the modal opened gets it back on
 *     close, if it is still in the document — and not if it is not, because a
 *     detached node is the same as nothing.
 *
 * **One addition to the dashboard's copy**: `labelledBy`. The dashboard names
 * its dialogs with `aria-label`, which is right where the panel has no visible
 * heading; this one opens under its own `.modal-head`, and pointing the dialog
 * at that element is what keeps the accessible name and the name on screen from
 * drifting apart. `label` is still required, and is what an assistive reader
 * gets when no `labelledBy` is given.
 */
export function Modal({
  label,
  labelledBy,
  onClose,
  children,
}: {
  label: string;
  /** The id of the panel's own heading, when it has one. */
  labelledBy?: string;
  onClose: () => void;
  children: ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  /* Captured while the component is being constructed rather than in the effect,
     so it is the element the reader actually left: by the time an effect runs,
     focus may already have moved. */
  const opener = useRef<Element | null>(
    typeof document === 'undefined' ? null : document.activeElement,
  );

  useEffect(() => {
    const node = panel.current;
    if (!node) return;
    (focusable(node)[0] ?? node).focus();

    return () => {
      const previous = opener.current;
      if (previous instanceof HTMLElement && previous.isConnected) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const node = panel.current;
      if (!node) return;
      const stops = focusable(node);
      if (stops.length === 0) {
        event.preventDefault();
        node.focus();
        return;
      }
      const first = stops[0] as HTMLElement;
      const last = stops[stops.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !node.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !node.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* The click-away is on the scrim's own element rather than on a bubbling path,
     Escape and the tab trap are bound to the window, and React keeps context and
     event propagation running down the tree the component is *written* in, not
     the one it renders into. */
  return (
    <Portal>
      <div
        className="modal"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <div
          className="modal-panel"
          role="dialog"
          aria-label={labelledBy ? undefined : label}
          aria-labelledby={labelledBy}
          aria-modal="true"
          ref={panel}
          tabIndex={-1}
        >
          {children}
        </div>
      </div>
    </Portal>
  );
}

/**
 * The panel's tab stops, in document order. A disabled control is not a stop and
 * neither is anything taken out of the order explicitly — which is what keeps
 * the panel's own `tabIndex={-1}` from being counted as the first one.
 */
function focusable(root: HTMLElement): HTMLElement[] {
  const selector =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  return [...root.querySelectorAll<HTMLElement>(selector)];
}
